import React from 'react';
import PlanNameWithProject from '../../../components/common/PlanNameWithProject/PlanNameWithProject';
import { formatCurrency } from '../../../utils';
import type {
  CheckoutAddressData,
  CheckoutCardData,
  CheckoutPayerData,
  CheckoutPersonalData,
  CheckoutPlanLike,
  PaymentMethod,
} from '../checkout.types';
import {
  calculateCheckoutTotalCents,
  formatCheckoutPhoneDisplay,
  formatLoyaltyLabel,
  getBillingCycleLabel,
  getBillingCyclePriceSuffix,
  getPaymentMethodLabel,
  getRecurringBillingLabel,
  isSinglePaymentPlan,
  maskCardNumberForDisplay,
  parsePlanBenefits,
} from '../checkout.utils';
import { formatIsoDateToMasked } from '../../../utils/masks';
import styles from './CheckoutShared.module.css';

interface OrderSummaryProps {
  personal: CheckoutPersonalData;
  address: CheckoutAddressData;
  selectedPlan: CheckoutPlanLike | null;
  paymentMethod: PaymentMethod | null;
  discountCents: number;
  card?: CheckoutCardData;
  payer?: CheckoutPayerData | null;
  useStudentDataAsPayer?: boolean;
  showPlanDetails?: boolean;
  /** Checkout resumido: só plano + pagamento. */
  compact?: boolean;
}

const formatAddressLine = (address: CheckoutAddressData): string => {
  const parts = [
    address.street,
    address.number ? `nº ${address.number}` : '',
    address.complement,
    address.neighborhood,
    [address.city, address.state].filter(Boolean).join(' - '),
    address.cep,
    address.country,
  ].filter(Boolean);

  return parts.join(', ');
};

/** Endereço do pagador: dados da aluna quando o checkbox foi usado; senão CEP/número/complemento informados. */
const resolvePayerAddressForDisplay = (
  address: CheckoutAddressData,
  payer: CheckoutPayerData,
  useStudentDataAsPayer: boolean,
): CheckoutAddressData => {
  if (useStudentDataAsPayer) {
    return address;
  }

  return {
    cep: payer.postalCode,
    street: '',
    number: payer.addressNumber,
    complement: payer.addressComplement,
    neighborhood: '',
    city: '',
    state: '',
    country: address.country || 'BR',
  };
};

const OrderSummary: React.FC<OrderSummaryProps> = ({
  personal,
  address,
  selectedPlan,
  paymentMethod,
  discountCents,
  card,
  payer = null,
  useStudentDataAsPayer = false,
  showPlanDetails = false,
  compact = false,
}) => {
  const totalCents = calculateCheckoutTotalCents(selectedPlan, discountCents);
  const planBenefits = selectedPlan ? parsePlanBenefits(selectedPlan.benefits) : [];
  const showPayerSection = !compact
    && paymentMethod === 'credit_card'
    && Boolean(payer?.name.trim());
  const payerAddress = payer
    ? resolvePayerAddressForDisplay(address, payer, useStudentDataAsPayer)
    : null;

  return (
    <div className={styles.orderSummary}>
      {!compact && (
        <>
          <section className={styles.orderSection}>
            <h3 className={styles.orderSectionTitle}>Dados pessoais</h3>
            <ul className={styles.orderList}>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Nome</span>
                <span>{personal.fullName || '—'}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>E-mail</span>
                <span>{personal.email || '—'}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>CPF</span>
                <span>{personal.cpf || '—'}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Celular</span>
                <span>{formatCheckoutPhoneDisplay(personal.phone, personal.callingCode)}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Nascimento</span>
                <span>{personal.birthDate ? formatIsoDateToMasked(personal.birthDate) : '—'}</span>
              </li>
            </ul>
          </section>

          <section className={styles.orderSection}>
            <h3 className={styles.orderSectionTitle}>Endereço</h3>
            <p className={styles.summaryEmpty}>{formatAddressLine(address) || '—'}</p>
          </section>
        </>
      )}

      {showPayerSection && payer && payerAddress && (
        <section className={styles.orderSection}>
          <h3 className={styles.orderSectionTitle}>Dados do pagador</h3>
          <ul className={styles.orderList}>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Nome</span>
              <span>{payer.name || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>E-mail</span>
              <span>{payer.email || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>CPF</span>
              <span>{payer.cpf || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Celular</span>
              <span>{formatCheckoutPhoneDisplay(payer.phone, payer.callingCode)}</span>
            </li>
          </ul>
          <h3 className={styles.orderSectionTitle}>Endereço do pagador</h3>
          <ul className={styles.orderList}>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>CEP</span>
              <span>{payerAddress.cep || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Rua</span>
              <span>{payerAddress.street || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Número</span>
              <span>{payerAddress.number || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Complemento</span>
              <span>{payerAddress.complement || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Bairro</span>
              <span>{payerAddress.neighborhood || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Cidade</span>
              <span>{payerAddress.city || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Estado</span>
              <span>{payerAddress.state || '—'}</span>
            </li>
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>País</span>
              <span>{payerAddress.country || '—'}</span>
            </li>
          </ul>
        </section>
      )}

      <section className={styles.orderSection}>
        <h3 className={styles.orderSectionTitle}>{compact ? 'Plano' : 'Plano contratado'}</h3>
        <ul className={styles.orderList}>
          <li className={styles.orderItem}>
            <span className={styles.orderItemLabel}>Plano</span>
            <span className={styles.orderItemValue}>
              <PlanNameWithProject name={selectedPlan?.name} project={selectedPlan?.project} />
            </span>
          </li>
          {showPlanDetails && selectedPlan && (
            <>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Valor</span>
                <span>
                  {formatCurrency(selectedPlan.price_cents / 100)}
                  {getBillingCyclePriceSuffix(selectedPlan.billing_cycle)}
                </span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Ciclo</span>
                <span>{getBillingCycleLabel(selectedPlan.billing_cycle)}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Cobrança</span>
                <span>{getRecurringBillingLabel(selectedPlan)}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Fidelidade</span>
                <span>{formatLoyaltyLabel(selectedPlan.loyalty_months)}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Renovação automática</span>
                <span>{selectedPlan.auto_renew && !isSinglePaymentPlan(selectedPlan) ? 'Sim' : 'Não'}</span>
              </li>
              {selectedPlan.description && (
                <li className={styles.orderItem}>
                  <span className={styles.orderItemLabel}>Descrição</span>
                  <span>{selectedPlan.description}</span>
                </li>
              )}
            </>
          )}
          {!showPlanDetails && (
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Ciclo</span>
              <span>
                {selectedPlan
                  ? getBillingCycleLabel(selectedPlan.billing_cycle)
                  : '—'}
              </span>
            </li>
          )}
        </ul>
        {showPlanDetails && planBenefits.length > 0 && (
          <>
            <p className={styles.orderItemLabel}>Benefícios</p>
            <ul className={styles.orderBenefitsList}>
              {planBenefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className={styles.orderSection}>
        <h3 className={styles.orderSectionTitle}>Pagamento</h3>
        <ul className={styles.orderList}>
          <li className={styles.orderItem}>
            <span className={styles.orderItemLabel}>Forma</span>
            <span>{getPaymentMethodLabel(paymentMethod)}</span>
          </li>
          {paymentMethod === 'credit_card' && card && (
            <>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Pagador</span>
                <span>{payer?.name?.trim() || card.holderName || '—'}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Nome no cartão</span>
                <span>{card.holderName || '—'}</span>
              </li>
              <li className={styles.orderItem}>
                <span className={styles.orderItemLabel}>Cartão</span>
                <span>{maskCardNumberForDisplay(card.number)}</span>
              </li>
            </>
          )}
          {discountCents > 0 && (
            <li className={styles.orderItem}>
              <span className={styles.orderItemLabel}>Desconto</span>
              <span>- {formatCurrency(discountCents / 100)}</span>
            </li>
          )}
          <li className={styles.orderItem}>
            <span className={styles.orderItemLabel}>Valor total</span>
            <span>
              {formatCurrency(totalCents / 100)}
              {selectedPlan ? getBillingCyclePriceSuffix(selectedPlan.billing_cycle) : ''}
            </span>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default OrderSummary;
